import React from "react";
import {
  Clock,
  MessageSquare,
  ListChecks,
  CheckCircle2,
  BookOpen,
  Image,
  X,
  ChevronLeft,
  ChevronRight,
  FileDown,
} from "lucide-react";
import { codeArticles } from "../data/codeArticles";
import { supabase } from "../supabaseClient";

// Utility function to safely parse and format dates
const formatDateSafely = (dateString) => {
  if (!dateString) return "";

  // Try parsing different date formats
  let date;

  // First try parsing as is
  date = new Date(dateString);

  // If invalid, try European format (DD-MM-YYYY)
  if (isNaN(date.getTime())) {
    const [day, month, year] = dateString.split("-");
    date = new Date(year, month - 1, day);
  }

  // If still invalid, try other common formats
  if (isNaN(date.getTime())) {
    // Try Dutch format with spaces
    const parts = dateString.split(" ");
    if (parts.length === 3) {
      const monthMap = {
        januari: 0,
        februari: 1,
        maart: 2,
        april: 3,
        mei: 4,
        juni: 5,
        juli: 6,
        augustus: 7,
        september: 8,
        oktober: 9,
        november: 10,
        december: 11,
      };
      const day = parseInt(parts[0]);
      const month = monthMap[parts[1].toLowerCase()];
      const year = parseInt(parts[2]);
      if (!isNaN(day) && month !== undefined && !isNaN(year)) {
        date = new Date(year, month, day);
      }
    }
  }

  // If date is still invalid, return the original string
  if (isNaN(date.getTime())) {
    console.warn(`Unable to parse date: ${dateString}`);
    return dateString;
  }

  // Format the date in Dutch format
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

// Utility function to handle article content extraction
const getArticleContent = (articleData, reference) => {
  if (!articleData || !reference) return null;

  // Extract the base article number and subsection
  const [baseArticle, ...rest] = reference.split(" ");
  const hasSubsection = rest.join(" ").toLowerCase().includes("onder");

  if (!hasSubsection) {
    return {
      header: articleData.header,
      content: articleData.text,
    };
  }

  // Extract subsection letter
  const subsectionLetter = rest
    .join(" ")
    .toLowerCase()
    .split("onder")[1]
    .trim();

  if (articleData.subsections && articleData.subsections[subsectionLetter]) {
    return {
      header: `${articleData.header} - ${subsectionLetter}`,
      introduction: articleData.introduction,
      content: articleData.subsections[subsectionLetter],
    };
  }

  // Fallback to full text if subsection not found
  return {
    header: articleData.header,
    content: articleData.text,
  };
};

const ComplaintAnalysis = ({ data, formatDate }) => {
  const [selectedImage, setSelectedImage] = React.useState(null);
  const [images, setImages] = React.useState([]);

  // In your ComplaintAnalysis component, add this effect:
  React.useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedImage]);

  // Add this function to handle image navigation
  const navigateImage = (direction) => {
    const currentIndex = images.findIndex((img) => img.id === selectedImage.id);
    if (direction === "next" && currentIndex < images.length - 1) {
      setSelectedImage(images[currentIndex + 1]);
    } else if (direction === "prev" && currentIndex > 0) {
      setSelectedImage(images[currentIndex - 1]);
    }
  };

  React.useEffect(() => {
    const fetchImages = async () => {
      // First log the full data to see its structure
      console.log("Full data:", data);

      // Try to get zaak_id from multiple possible locations
      const zaakId =
        data.basis_informatie?.zaak_id ||
        data.id ||
        window.location.pathname.split("/").pop();

      console.log("Using zaakId:", zaakId);

      if (!zaakId) {
        console.log("No zaak_id found");
        return;
      }

      const { data: zaakImages, error } = await supabase
        .from("ZaakImages")
        .select("*")
        .eq("zaak_id", zaakId);

      if (error) {
        console.error("Supabase error:", error);
        return;
      }

      console.log("Found images:", zaakImages);
      setImages(zaakImages || []);
    };

    fetchImages();
  }, [data]);

  if (!data) return null;

  const {
    basis_informatie,
    standpunten,
    beoordeling,
    tijdlijn,
    relevante_artikelen,
    onderwerp,
  } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Beoordeling */}
        {beoordeling && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-nn-500 text-lg font-semibold mb-4 flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-nn-500" />
              Beoordeling
            </h2>
            <div className="space-y-4">
              {beoordeling.hoofdpunten_oordeel?.map((point, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-nn-teal-500 flex-shrink-0 mt-0.5" />
                  <span className="text-nn-gray-700">{point}</span>
                </div>
              ))}
              {beoordeling.motivering && (
                <div className="mt-6 pt-4">
                  <h3 className="text-sm font-medium text-nn-gray-500 mb-2">
                    Motivering
                  </h3>
                  <p className="text-nn-gray-700">{beoordeling.motivering}</p>
                </div>
              )}
              {beoordeling.beslissing && (
                <div className="mt-6 pt-4">
                  <h3 className="text-sm font-medium text-nn-gray-500 mb-2">
                    Beslissing
                  </h3>
                  <p className="text-nn-gray-700">{beoordeling.beslissing}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reclame-uiting + Images */}
        <div className="space-y-6">
          {onderwerp?.reclameuiting && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-nn-500 text-lg font-semibold mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-nn-500" />
                Reclame-uiting
              </h2>
              <div className="prose max-w-none">
                <div className="text-nn-gray-700">
                  {onderwerp.reclameuiting}
                </div>
              </div>
            </div>
          )}

          {images?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-nn-500 text-lg font-semibold mb-4 flex items-center gap-2">
                <Image className="h-5 w-5 text-nn-500" />
                Promotionele uitingen
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {images.map((image) => (
                  <div
                    key={image.id}
                    className="relative aspect-square cursor-pointer overflow-hidden rounded-lg bg-gray-100"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image.image_url}
                      alt={`Afbeelding ${image.id}`}
                      className="object-cover w-full h-full hover:scale-105 transition-transform"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tijdlijn */}
        {tijdlijn?.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-nn-500 text-lg font-semibold mb-6 flex items-center gap-2">
              <Clock className="h-5 w-5 text-nn-500" />
              Tijdlijn
            </h2>
            <div className="relative pl-8">
              <div
                className="absolute left-[11px] w-0.5 bg-nn-gray-200"
                style={{ top: "6px", bottom: "6px" }}
              />
              <div className="space-y-8">
                {tijdlijn.map((event, idx) => (
                  <div key={idx} className="relative flex items-start">
                    <div
                      className="absolute -left-[24.5px] w-3 h-3 rounded-full bg-white border-2 border-nn-500"
                      style={{ top: "50%", transform: "translateY(-50%)" }}
                    />
                    <div className="flex-1 bg-nn-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-nn-500 mb-1">
                        {formatDateSafely(event.datum)}
                      </div>
                      <div className="text-nn-gray-700">{event.feit}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Second Row: Standpunt klager, Standpunt verweerder */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Standpunt klager */}
        {standpunten?.klager && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-nn-500 text-lg font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-nn-500" />
              Standpunt klager{" "}
              {basis_informatie?.klager && `(${basis_informatie.klager})`}
            </h2>
            <div className="space-y-2">
              <ul className="list-disc list-inside space-y-1 text-nn-gray-700">
                {standpunten.klager.argumenten?.map((arg, idx) => (
                  <li key={idx}>{arg}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Standpunt verweerder */}
        {standpunten?.verweerder && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-nn-500 text-lg font-semibold mb-4 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-nn-500" />
              Standpunt verweerder{" "}
              {basis_informatie?.verweerder &&
                `(${basis_informatie.verweerder})`}
            </h2>
            <div className="space-y-2">
              <ul className="list-disc list-inside space-y-1 text-nn-gray-700">
                {standpunten.verweerder.argumenten?.map((arg, idx) => (
                  <li key={idx}>{arg}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Third Row: Relevante artikelen */}
      {relevante_artikelen?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-nn-500 text-lg font-semibold flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-nn-500" />
              Relevante artikelen uit de Code
            </h2>

            <a
              href="https://mwpkmlumjrauwywhfgfo.supabase.co/storage/v1/object/public/pdf-uploads/Bundel%20Gedragscode%20Jan%201%202024%20(1).pdf?t=2025-01-09T09%3A28%3A38.825Z"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-nn-500 hover:text-nn-600 transition-colors duration-150"
            >
              <FileDown className="h-4 w-4" />
              <span>Download Gedragscode</span>
            </a>
          </div>

          <div className="space-y-6">
            {relevante_artikelen.map((artikel, idx) => {
              const baseArticleNum = artikel.artikel.split(" ")[0];
              const articleContent = getArticleContent(
                codeArticles[baseArticleNum],
                artikel.artikel
              );

              if (!articleContent) return null;

              return (
                <div key={idx} className="bg-nn-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-nn-gray-900 mb-3">
                    Artikel {baseArticleNum} -{" "}
                    {codeArticles[baseArticleNum]?.header}
                    {artikel.artikel.toLowerCase().includes("onder") &&
                      ` (${artikel.artikel.split("onder")[1].trim()})`}
                  </h3>

                  {articleContent.introduction && (
                    <p className="text-nn-gray-700 mb-2 italic">
                      {articleContent.introduction}
                    </p>
                  )}

                  <p className="text-nn-gray-700 mb-4">
                    {articleContent.content}
                  </p>

                  {artikel.reden_relevant && (
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-nn-gray-700">
                        Waarom relevant:
                      </h4>
                      <p className="text-sm text-nn-gray-500">
                        {artikel.reden_relevant}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="max-w-4xl w-full bg-white rounded-lg p-4 flex flex-col items-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex justify-end mb-2">
              <button
                onClick={() => setSelectedImage(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {selectedImage.description && (
              <div className="w-full">
                <p className="text-gray-700">{selectedImage.description}</p>
              </div>
            )}
            <div className="relative w-full flex items-center justify-center">
              {/* Previous button */}
              {images.findIndex((img) => img.id === selectedImage.id) > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateImage("prev");
                  }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10"
                >
                  <ChevronLeft className="h-6 w-6 text-gray-700" />
                </button>
              )}

              <img
                src={selectedImage.image_url}
                alt={`Afbeelding ${selectedImage.id}`}
                className="max-w-full max-h-[70vh] object-contain mb-4"
              />

              {/* Next button */}
              {images.findIndex((img) => img.id === selectedImage.id) <
                images.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateImage("next");
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 z-10"
                >
                  <ChevronRight className="h-6 w-6 text-gray-700" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintAnalysis;